import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

interface SidebarItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  badge?: string;
  children?: SidebarItem[];
  roles?: string[];
}

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ 
  collapsed = false, 
  onToggle 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const menuItems: SidebarItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: '📊',
      path: '/dashboard'
    },
    {
      id: 'bookings',
      label: 'Bookings',
      icon: '📅',
      path: '/bookings',
      badge: '12'
    },
    {
      id: 'assignment',
      label: 'Assignment Board',
      icon: '🎯',
      path: '/assignments' // ✅ Fixed: changed from /assignment-board to /assignments
    },
    {
      id: 'fleet',
      label: 'Fleet Management',
      icon: '🚗',
      path: '/fleet',      
    },
    {
      id: 'finance',
      label: 'Finance',
      icon: '💰',
      path: '/finance',
      roles: ['admin', 'finance'],
      
    },
    {
      id: 'configuration',
      label: 'Configuration',
      icon: '⚙️',
      path: '/configuration',
      roles: ['admin'],
      
    },
    /*{
      id: 'zones',
      label: 'Zones',
      icon: '🗺️',
      path: '/zones'
    },
    {
      id: 'promotions',
      label: 'Promotions',
      icon: '🎁',
      path: '/promotions'
    },
    {
      id: 'templates',
      label: 'Templates',
      icon: '📝',
      path: '/templates'
    },*/
    // ✅ Commented out since /audit route is also commented out in App.tsx
    /*{
      id: 'audit',
      label: 'Audit Logs',
      icon: '🔍',
      path: '/audit',
      roles: ['admin']
    }*/
  ];

  const hasPermission = (item: SidebarItem): boolean => {
    if (!item.roles || item.roles.length === 0) return true;
    return item.roles.includes(user?.role || '');
  };

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isParentActive = (item: SidebarItem) => {
    if (isActive(item.path)) return true;
    return item.children?.some(child => isActive(child.path)) || false;
  };

  const renderMenuItem = (item: SidebarItem, level: number = 0) => {
    if (!hasPermission(item)) return null;

    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const isItemActive = isActive(item.path);
    const isParentItemActive = isParentActive(item);

    return (
      <div key={item.id}>
        <div
          className={`
            flex items-center px-3 py-2 rounded-lg cursor-pointer transition-all duration-200
            ${level > 0 ? 'ml-6' : ''}
            ${isItemActive 
              ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-600' 
              : isParentItemActive 
                ? 'bg-blue-50 text-blue-600' 
                : 'text-gray-700 hover:bg-gray-100'
            }
            ${collapsed && level === 0 ? 'justify-center' : ''}
          `}
          onClick={() => {
            if (hasChildren && !collapsed) {
              toggleExpanded(item.id);
            } else {
              navigate(item.path);
            }
          }}
        >
          <span className="text-lg flex-shrink-0">
            {item.icon}
          </span>
          
          {!collapsed && (
            <>
              <span className="ml-3 flex-1 text-sm font-medium">
                {item.label}
              </span>
              
              {item.badge && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  {item.badge}
                </span>
              )}
              
              {hasChildren && (
                <span className={`ml-2 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}>
                  ▶
                </span>
              )}
            </>
          )}
        </div>

        {hasChildren && isExpanded && !collapsed && (
          <div className="mt-1 space-y-1">
            {item.children!.map(child => renderMenuItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`
      fixed left-0 top-0 h-full bg-white border-r border-gray-200 transition-all duration-300 z-40
      ${collapsed ? 'w-16' : 'w-64'}
    `}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        {!collapsed && (
          <h1 className="text-xl font-bold text-gray-900">
            Business Portal
          </h1>
        )}
        <button
          onClick={onToggle}
          className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <span className="text-lg">
            {collapsed ? '▶' : '◀'}
          </span>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2">
          {menuItems.map(item => renderMenuItem(item))}
        </div>
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-200 p-4">
        {!collapsed ? (
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
              {user?.firstName?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.role}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
              {user?.firstName?.charAt(0) || 'U'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};